import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { store } from "@/store/Store";
import { Grade } from "@/ts/objects/common/Grade";
import { appStateStoreGlobal } from "@/store/AppStateStore";
import { NECurriculum, NECurriculumTree } from "@/ts/objects/curriculum/value/NECurriculum";
import { NECEvaluation } from "@/ts/objects/curriculum/value/NECEvaluation";
import { EECurriculum, EECurriculumTree } from "@/ts/objects/curriculum/value/EECurriculum";
import { EECJournalTree } from "@/ts/objects/curriculum/value/EECJournal";
import { CurriculumRepository } from "@/ts/repositories/CurriculumRepository";
import { Err } from "@/ts/objects/Err";
import { CurriculumTabName, isCurriculumTabName } from "@/ts/objects/curriculum/value/CurriculumTabName";
import { CurriculumPeriodMode, isCurriculumPeriodMode } from "@/ts/objects/curriculum/value/CurriculumPeriodMode";
import { CurriculumCollectionId, isCurriculumCollectionId } from "@/ts/objects/curriculum/value/CurriculumCollectionId";
import log from "loglevel";
import { Loadable, LoadableError, LoadableIdle, LoadableLoading } from "@/ts/Loadable";
import { isMonthValue, MonthValue } from "@/ts/objects/common/MonthValue";

export type CurriculumRoute = {
  readonly studentUserId: string | null;
  readonly schoolYear: number | null;
  readonly tab: CurriculumTabName | null;
  readonly collectionId: CurriculumCollectionId | null;
  readonly periodMode: CurriculumPeriodMode | null;
  readonly month: MonthValue | null;
  readonly curriculumId: string | null;
};

@Module({ dynamic: true, store, name: "curriculumStoreS", namespaced: true })
export class CurriculumStoreS extends VuexModule {
  get currentPath(): string {
    return appStateStoreGlobal.route.path;
  }

  get currentRoute(): CurriculumRoute {
    const name = appStateStoreGlobal.route.name;
    const m = /^S\.Curriculum\.(\w+)\.(\w+)\.(\w+)/.exec(name);
    if (m === null) {
      log.debug(`CurriculumStoreS.currentRoute: Failed.`);
      return {
        studentUserId: null,
        schoolYear: null,
        tab: null,
        collectionId: null,
        periodMode: null,
        month: null,
        curriculumId: null,
      };
    }

    const studentUserId = appStateStoreGlobal.studentOrGuardianState?.studentUserId() ?? null;

    const _schoolYear = parseInt(appStateStoreGlobal.route.params["curriculumSchoolYear"], 10);
    const schoolYear = !isNaN(_schoolYear) && 1 <= _schoolYear && _schoolYear <= 9999 ? _schoolYear : null;

    const tab = isCurriculumTabName(m[1]) ? m[1] : null;
    const collectionId = isCurriculumCollectionId(m[2]) ? m[2] : null;
    const periodMode = isCurriculumPeriodMode(m[3]) ? m[3] : null;

    const _month = parseInt(appStateStoreGlobal.route.params["curriculumMonth"], 10);
    const month = isMonthValue(_month) ? _month : null;

    const _curriculumId = appStateStoreGlobal.route.params["curriculumId"];
    const curriculumId = _curriculumId !== "-" ? _curriculumId : null;

    const result = {
      studentUserId,
      schoolYear,
      tab,
      collectionId,
      periodMode,
      month,
      curriculumId,
    };
    log.debug(`CurriculumStoreS.currentRoute: result=${JSON.stringify(result)}`);
    return result;
  }

  /**
   * 選択中の年度。
   */
  get selectedSchoolYear(): number | null {
    return this.currentRoute.schoolYear;
  }

  /**
   * 児童生徒の学年。
   */
  get grade(): Grade | null {
    const schoolYear = this.selectedSchoolYear;
    if (schoolYear === null) return null;
    return appStateStoreGlobal.studentOrGuardianState?.studentGradeOfSchoolYear(schoolYear) ?? null;
  }

  /**
   * 児童生徒ID。
   */
  get studentUserId(): string | null {
    return this.currentRoute.studentUserId;
  }

  /**
   * 選択中のタブ。
   * 書く/見る/未選択(null)。
   */
  get tab(): CurriculumTabName | null {
    return this.currentRoute.tab;
  }

  /**
   * 表示期間のモード。
   * 月別表示/年間表示/未選択(null)。
   */
  get periodMode(): CurriculumPeriodMode | null {
    return this.currentRoute.periodMode;
  }

  /**
   * 今月。
   */
  get thisMonth(): MonthValue {
    return appStateStoreGlobal.now.month;
  }

  /**
   * 選択中の月。
   */
  get selectedMonth(): MonthValue | null {
    return this.currentRoute.month;
  }

  /**
   * 選択中の教科のコレクションID。
   * "neCurriculums" or "eeCurriculums"。
   */
  get selectedCurriculumCollectionId(): CurriculumCollectionId | null {
    return this.currentRoute.collectionId;
  }

  /**
   * 選択中の教科のリソース名。
   */
  get selectedCurriculumRname(): string | null {
    const collectionId = this.selectedCurriculumCollectionId;
    const id = this.currentRoute.curriculumId;

    if (collectionId === null || id === null) return null;

    return `/${collectionId}/${id}`;
  }

  /**
   * 選択中の(年度, 学年, 児童生徒)の、全データをロードする。
   * ※ よって、年度, 学年, 児童生徒 のいずれかが変更されたとき、リロードが必要。
   */
  @Action
  async loadAllDataOfSelectedYear(curriculumRepository: CurriculumRepository) {
    // TODO たぶん大丈夫だとは思うが、教科学習画面にいるとき、保護者で児童生徒を切り替えたときに、前の児童生徒IDでロードしに行かないか確認する。
    const schoolYear = this.selectedSchoolYear;
    const grade = this.grade;
    const studentUserId = this.studentUserId;
    if (schoolYear === null || grade === null || studentUserId === null) {
      this.context.commit("setAllData", {
        allNECurriculums: new LoadableIdle(),
        allNECEvaluations: new LoadableIdle(),
        allEECurriculums: new LoadableIdle(),
        allEECJournals: new LoadableIdle(),
      });
      return;
    }

    this.context.commit("setAllData", {
      allNECurriculums: new LoadableLoading(),
      allNECEvaluations: new LoadableLoading(),
      allEECurriculums: new LoadableLoading(),
      allEECJournals: new LoadableLoading(),
    });

    log.debug(`CurriculumStore.loadAllData: schoolYear=${schoolYear}, grade=${grade}`);

    const [allNECurriculums, allEECurriculums] = await Promise.all([
      // allNECurriculums
      (async () => {
        log.debug(`CurriculumStore.loadAllData: neCurriculums Loadable: schoolYear=${schoolYear}, grade=${grade}`);
        const resp = await curriculumRepository.listNECurriculums(schoolYear, grade);
        if (resp instanceof Err) return null;
        return resp;
      })(),
      // allEECurriculums
      (async () => {
        const resp = await curriculumRepository.listEECurriculums(schoolYear, grade);
        if (resp instanceof Err) return null;
        return resp;
      })(),
    ]);

    if (allNECurriculums === null || allEECurriculums === null) {
      this.context.commit("setAllData", {
        allNECurriculums: new LoadableError(),
        allNECEvaluations: new LoadableError(),
        allEECurriculums: new LoadableError(),
        allEECJournals: new LoadableError(),
      });
      return;
    }

    const [allNECEvaluations, allEECJournals] = await Promise.all([
      // allNECEvaluations
      (async () => {
        const resp = await curriculumRepository.listNECEvaluations(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          studentUserId,
          schoolYear,
          grade
        );
        if (resp instanceof Err) return null;
        return resp;
      })(),
      // allEECJournals
      (async () => {
        const resp = await curriculumRepository.listEECJournals(undefined, undefined, studentUserId, schoolYear, grade);
        if (resp instanceof Err) return null;
        return resp;
      })(),
    ]);

    if (allNECEvaluations === null || allEECJournals === null) {
      this.context.commit("setAllData", {
        allNECurriculums: new LoadableError(),
        allNECEvaluations: new LoadableError(),
        allEECurriculums: new LoadableError(),
        allEECJournals: new LoadableError(),
      });
      return;
    }

    this.context.commit("setAllData", {
      allNECurriculums: Loadable.fromValue(allNECurriculums),
      allNECEvaluations: Loadable.fromValue(allNECEvaluations),
      allEECurriculums: Loadable.fromValue(allEECurriculums),
      allEECJournals: Loadable.fromValue(allEECJournals),
    });
  }

  @Mutation
  setAllData(payload: {
    allNECurriculums: Loadable<NECurriculumTree[]>;
    allNECEvaluations: Loadable<NECEvaluation[]>;
    allEECurriculums: Loadable<EECurriculumTree[]>;
    allEECJournals: Loadable<EECJournalTree[]>;
  }) {
    this.neCurriculumTrees = payload.allNECurriculums;
    this.allNECEvaluations = payload.allNECEvaluations;
    this.eeCurriculumTrees = payload.allEECurriculums;
    this.allEECJournalTrees = payload.allEECJournals;
  }

  get isDataIdleOrLoading(): boolean {
    return this.neCurriculumTrees.isIdleOrLoading || this.eeCurriculumTrees.isIdleOrLoading;
  }

  get isDataLoaded(): boolean {
    return (
      this.neCurriculumTrees.isDataLoaded &&
      this.allNECEvaluations.isDataLoaded &&
      this.eeCurriculumTrees.isDataLoaded &&
      this.allEECJournalTrees.isDataLoaded
    );
  }

  @Action
  async loadJournals(curriculumRepository: CurriculumRepository) {
    const schoolYear = this.selectedSchoolYear;
    const grade = this.grade;
    const studentUserId = this.studentUserId;
    const allEECurriculumsData = this.eeCurriculumTrees?.data ?? null;
    if (schoolYear === null || grade === null || studentUserId === null || allEECurriculumsData === null) {
      this.context.commit("setEECJournals", new LoadableIdle());
      return;
    }

    this.context.commit("setEECJournals", new LoadableLoading());

    const resp = await curriculumRepository.listEECJournals(undefined, undefined, studentUserId, schoolYear, grade);
    if (resp instanceof Err) {
      this.context.commit("setEECJournals", new LoadableError());
      return;
    }

    this.context.commit("setEECJournals", Loadable.fromValue(resp));
  }

  @Mutation
  setEECJournals(value: Loadable<EECJournalTree[]>) {
    this.allEECJournalTrees = value;
  }

  /**
   * 選択中の(年度, 学年)の、数値評価教科のリスト(ツリー)。
   */
  neCurriculumTrees: Loadable<NECurriculumTree[]> = new LoadableIdle();

  /**
   * 選択中の(年度, 学年)の、数値評価教科のリスト。
   */
  get neCurriculums(): Loadable<NECurriculum[]> {
    return Loadable.fromLoadable(this.neCurriculumTrees, trees => trees.map(t => t.self));
  }

  /**
   * 選択中の数値評価教科(ツリー)。
   */
  get neCurriculumTree(): Loadable<NECurriculumTree | null> {
    const _neCurriculums = this.neCurriculumTrees;
    if (_neCurriculums === null) return new LoadableIdle();

    const selectedCurriculumRname = this.selectedCurriculumRname;

    return Loadable.fromLoadable(_neCurriculums, neCurriculums => {
      if (selectedCurriculumRname === null) return null;
      return neCurriculums.find(c => c.self.resourceName === selectedCurriculumRname) ?? null;
    });
  }

  /**
   * 選択中の数値評価教科。
   */
  get neCurriculum(): Loadable<NECurriculum | null> {
    return Loadable.fromLoadable(this.neCurriculumTree, neCurriculumTree => neCurriculumTree?.self ?? null);
  }

  /**
   * 選択中の(年度, 学年, 児童生徒)の、数値評価教科の評価。
   */
  allNECEvaluations: Loadable<NECEvaluation[]> = new LoadableIdle();

  /**
   * 選択中の数値評価教科・児童生徒でフィルタ済の、数値評価教科の評価。
   */
  get necEvaluationsOfYear(): Loadable<NECEvaluation[]> {
    const _neCurriculum = this.neCurriculumTree;
    const allNECEvaluations = this.allNECEvaluations;
    if (_neCurriculum === null || allNECEvaluations === null) return new LoadableIdle();

    const studentUserId = this.studentUserId;

    return Loadable.fromLoadable2(allNECEvaluations, _neCurriculum, (allNECEvaluations, neCurriculum) => {
      if (neCurriculum === null) return [];
      return allNECEvaluations.filter(e => e.necId === neCurriculum.self.necId && e.studentUserId === studentUserId);
    });
  }

  /**
   * 選択中の数値評価教科・児童生徒・月でフィルタ済の、数値評価教科の評価。
   */
  get necEvaluationsOfMonth(): Loadable<NECEvaluation[]> {
    const _necEvaluationsOfYear = this.necEvaluationsOfYear;
    if (_necEvaluationsOfYear === null) return new LoadableIdle();

    const month = this.selectedMonth;

    return Loadable.fromLoadable(_necEvaluationsOfYear, necEvaluationsOfYear => {
      if (necEvaluationsOfYear === null) return [];
      return necEvaluationsOfYear.filter(e => e.month === month);
    });
  }

  /**
   * 選択中の(年度, 学年)の、文書評価教科のリスト(ツリー)。
   */
  eeCurriculumTrees: Loadable<EECurriculumTree[]> = new LoadableIdle();

  /**
   * 選択中の(年度, 学年)の、文書評価教科のリスト。
   */
  get eeCurriculums(): Loadable<EECurriculum[]> {
    return Loadable.fromLoadable(this.eeCurriculumTrees, trees => trees.map(t => t.self));
  }

  /**
   * 選択中の文書評価教科(ツリー)。
   */
  get eeCurriculumTree(): Loadable<EECurriculumTree | null> {
    const _eeCurriculumTrees = this.eeCurriculumTrees;
    if (_eeCurriculumTrees === null) return new LoadableIdle();

    const selectedCurriculumRname = this.selectedCurriculumRname;
    if (selectedCurriculumRname === null) return new LoadableIdle();

    return Loadable.fromLoadable(_eeCurriculumTrees, eeCurriculumTrees => {
      return eeCurriculumTrees.find(c => c.self.resourceName === selectedCurriculumRname) ?? null;
    });
  }

  /**
   * 選択中の文書評価教科。
   */
  get eeCurriculum(): Loadable<EECurriculum | null> {
    return Loadable.fromLoadable(this.eeCurriculumTree, eeCurriculumTree => eeCurriculumTree?.self ?? null);
  }

  /**
   * 選択中の(年度, 学年, 児童生徒)の、文書評価教科の学習活動記録。
   */
  allEECJournalTrees: Loadable<EECJournalTree[]> = new LoadableIdle();

  /**
   * 選択中の文書評価教科・児童生徒でフィルタ済の、文書評価教科の学習活動記録。
   */
  get eecJournalTreesOfYear(): Loadable<EECJournalTree[]> {
    const _allEECJournals = this.allEECJournalTrees;
    const _eeCurriculum = this.eeCurriculumTree;
    if (_allEECJournals === null || _eeCurriculum === null) return new LoadableIdle();

    const studentUserId = this.studentUserId;

    return Loadable.fromLoadable2(_allEECJournals, _eeCurriculum, (allEECJournals, eeCurriculum) => {
      if (eeCurriculum === null) return [];
      return allEECJournals.filter(
        s => s.self.eecId === eeCurriculum.self.eecId && s.self.studentUserId === studentUserId
      );
    });
  }

  /**
   * 選択中の文書評価教科・児童生徒・月でフィルタ済の、文書評価教科の学習記録。
   */
  get eecJournalTreesOfMonth(): Loadable<EECJournalTree[]> {
    const _eecJournalsOfYear = this.eecJournalTreesOfYear;
    if (_eecJournalsOfYear === null) return new LoadableIdle();

    const month = this.selectedMonth;
    if (month === null) return new LoadableIdle();

    return Loadable.fromLoadable(_eecJournalsOfYear, eecJournalsOfYear => {
      if (eecJournalsOfYear === null) return [];
      return eecJournalsOfYear.filter(j => j.self.month === month);
    });
  }

  /**
   * 最初の教科を取得する。
   */
  get firstCurriculumTree(): Loadable<NECurriculumTree | EECurriculumTree | null> {
    const _neCurriculums = this.neCurriculumTrees;
    const _eeCurriculums = this.eeCurriculumTrees;
    if (_neCurriculums === null || _eeCurriculums === null) return new LoadableIdle();
    return Loadable.fromLoadable2(_neCurriculums, _eeCurriculums, (neCurriculums, eeCurriculums) => {
      log.debug(
        `CurriculumStoreS.firstCurriculumTree: neCurriculums.length=${neCurriculums.length}, eeCurriculums.length=${eeCurriculums.length}`
      );
      if (neCurriculums.length >= 1) return neCurriculums[0];
      else if (eeCurriculums.length >= 1) return eeCurriculums[0];
      else return null;
    });
  }

  /**
   * 最初の文書評価教科を取得する。
   */
  get firstEECurriculumTree(): Loadable<EECurriculumTree | null> {
    const _eeCurriculums = this.eeCurriculumTrees;
    if (_eeCurriculums === null) return new LoadableIdle();
    return Loadable.fromLoadable(_eeCurriculums, eeCurriculums => {
      if (eeCurriculums.length >= 1) return eeCurriculums[0];
      else return null;
    });
  }

  // get path() {
  //   const _base = appStateStoreGlobal.studentOrGuardianBasePath;
  //
  //   return function(
  //     schoolYear: number | null,
  //     tab: CurriculumTabName,
  //     periodMode: CurriculumPeriodMode | null,
  //     month: MonthValue | null | undefined,
  //     resourceName: string | null
  //   ) {
  //     const _schoolYear = schoolYear ?? 2000;
  //
  //     switch (tab) {
  //       case "write": {
  //         const _resourceName = resourceName ?? "/eeCurriculums/-";
  //         return `${_base}/curriculum/${_schoolYear}/${tab}${_resourceName}`;
  //       }
  //       case "read": {
  //         const _periodMode = periodMode ?? "-";
  //         const _month = month ?? "-";
  //         const _resourceName = resourceName ?? "/neCurriculums/-";
  //         return `${_base}/curriculum/${_schoolYear}/${tab}/${_periodMode}/${_month}${_resourceName}`;
  //       }
  //     }
  //     return _base;
  //   };
  // }

  get pathOfSelectedYear() {
    const schoolYear = this.selectedSchoolYear;
    const getPath = appStateStoreGlobal.studentOrGuardianCurriculumPath;

    return function(
      tab: CurriculumTabName,
      periodMode: CurriculumPeriodMode | null,
      month: MonthValue | null | undefined,
      resourceName: string | null
    ) {
      return getPath(schoolYear, tab, periodMode, month, resourceName);
    };
  }

  get firstCurriculumWritePath(): Loadable<string> {
    const firstCurriculum = this.firstEECurriculumTree;
    if (firstCurriculum === null) return new LoadableIdle();
    return Loadable.fromLoadable(firstCurriculum, curriculum => {
      if (curriculum === null) return this.pathOfSelectedYear("write", null, null, null);
      return this.pathOfSelectedYear("write", null, null, curriculum.self.resourceName);
    });
  }

  get firstCurriculumReadPath(): Loadable<string> {
    const firstCurriculum = this.firstCurriculumTree;
    if (firstCurriculum === null) return new LoadableIdle();
    return Loadable.fromLoadable(firstCurriculum, curriculum => {
      if (curriculum === null) return this.pathOfSelectedYear("read", "monthly", null, null);
      return this.pathOfSelectedYear("read", "monthly", appStateStoreGlobal.now.month, curriculum.self.resourceName);
    });
  }

  get defaultPath(): Loadable<string> {
    if (appStateStoreGlobal.isAppUserStudent) return this.firstCurriculumWritePath;
    if (appStateStoreGlobal.isAppUserGuardian) return this.firstCurriculumReadPath;
    return Loadable.fromValue("");
  }

  get defaultPathOfSelectedTab(): Loadable<string> {
    if (this.tab === "write") return this.firstCurriculumWritePath;
    if (this.tab === "read") return this.firstCurriculumReadPath;
    return Loadable.fromValue("");
  }
}
